///
/// EarthBrowser
///
/// Dataset Layer Manager
///
/// Central coordinator for all dataset layers.
/// Manages sync, polling, visibility, and scene graph integration.
///
/// Created by Matt Giger on 1/2/2026
/// Copyright (c) 2026 Geodata Labs
///

import { engineNotifier } from "@/earthbrowser/engineNotifications"
import { clearTargetFromHistory } from "@/utils/navigationHistory"
import { datasetUrl } from "@/utils/datasetUrl"
import { Constants } from "@/constants"
import { Database } from '@/earthbrowser/database/Database'
import type { EarthBrowserContext } from '@/earthbrowser/EarthBrowserContext'
import type { NodeBase } from '@stovokor/engine/NodeBase'
import type { FocusTarget } from '@stovokor/gestures/FocusTarget'
import { tableFromIPC } from "apache-arrow"
import { type ArrowRow, type DatasetLayer, type DatasetSceneObject, type DatasetFeature, type DatasetManifest } from './DatasetLayer'
import { formatBytes, type LayerLoadReport } from '@/earthbrowser/datasets/LoadState'
import { cFrameLaneExtensions, cFrameStepPrecedence, type TimeLane, type TimeLaneDetail, type TimeSpanMs } from '@/earthbrowser/datasets/TimeLane'

/** External (non-DatasetLayer) time-based layer: range plus optional frame detail */
export type ExternalTimeLaneProvider = () => (TimeSpanMs & Partial<TimeLaneDetail>) | null

/** External (non-DatasetLayer) layer's load state; null while it has nothing pending */
export type ExternalLoadStateProvider = () => Omit<LayerLoadReport, "id"> | null

type ArrowDownloader = (url: string) => Promise<ArrowRow[]>

type Catalog = {
	schemaVersion: 1
	generatedAt: string
	datasets: Array<{
		datasetId: string
		latestVersion: string
		manifestPath: string
		manifestUrl?: string
	}>
}

type HttpCacheRow = {
	url: string
	last_modified: string | null
	body: Uint8Array | null
}

type LayerTimer = {
	timer: number | null
	pollMs: number
	active: boolean
	lastSyncTime: number  // Timestamp of last sync (Date.now())
}

export type FocusSceneObjectOptions = {
	/** Place the camera at the focus destination immediately (no flight) */
	instant?: boolean
}

/**
 * Dataset Layer Manager
 *
 * Central coordinator for all dataset layers:
 * - Manages layer registration and scene graph integration
 * - Handles dataset sync from CDN (catalog, manifests, Arrow data)
 * - Coordinates polling for layers that need periodic updates
 * - Pauses/resumes polling based on page visibility
 * - Provides HTTP caching and version tracking utilities
 */
export class DatasetLayerManager {
	protected context: EarthBrowserContext
	protected container: NodeBase
	private readonly baseUrl = `${Constants.baseStorageUrl}/datasets`

	// Registered dataset layers
	private layers = new Map<string, DatasetLayer<DatasetFeature>>()

	// Time-range providers for time-based layers living outside the manager
	// (e.g. SynopticDatasetLayer, which is a plain scene node)
	private externalTimeRangeProviders = new Map<string, ExternalTimeLaneProvider>()
	private externalLoadStateProviders = new Map<string, ExternalLoadStateProvider>()

	// Polling timers per layer
	private layerTimers = new Map<string, LayerTimer>()

	// Visibility state
	private isPaused: boolean = false
	private visibilityHandler: (() => void) | null = null
	private pausedAt: number = 0  // When the page was hidden

	// Cached catalog (loaded once on startup, reused for polling)
	private catalogCache: Map<string, Catalog["datasets"][number]> | null = null

	// Listeners notified after a layer's data has been refreshed from the
	// database (initial cached-data load, post-sync, or manual refresh).
	// Lets UI (e.g. dataset browser windows) re-read tables when new data lands.
	private refreshListeners = new Map<string, Set<() => void>>()

	constructor(context: EarthBrowserContext, container: NodeBase) {
		this.context = context
		this.container = container
	}

	// ==================== Layer Registration ====================

	/**
	 * Register a dataset layer
	 */
	registerLayer(layer: DatasetLayer<DatasetFeature>): void {
		if (this.layers.has(layer.name)) {
			console.warn(`[DatasetLayerManager] Layer already registered: ${layer.name}`)
			return
		}

		this.layers.set(layer.name, layer)
		this.container.addNode(layer)

		// console.log(`[DatasetLayerManager] Registered layer: ${layer.name}`)
	}

	/**
	 * Register a time-range provider for a time-based layer that isn't a
	 * DatasetLayer managed here. The provider returns null when hidden/empty.
	 */
	registerTimeRangeProvider(id: string, provider: ExternalTimeLaneProvider): void {
		this.externalTimeRangeProviders.set(id, provider)
	}

	/**
	 * Register a load-state provider for a layer that isn't a DatasetLayer
	 * managed here, so it shows up in getLoadStates() beside the rest
	 */
	registerLoadStateProvider(id: string, provider: ExternalLoadStateProvider): void {
		this.externalLoadStateProviders.set(id, provider)
	}

	/**
	 * Every layer currently waiting on data, keyed by layer name: what it
	 * is waiting on (download, ingest, scene build, a time-series frame)
	 * and whether it is on the globe. Layers with nothing pending are
	 * omitted. Polled by the UI (useLayerLoadStates) to light the loading
	 * indicators on the Layers button, layer rows, legend chips and the
	 * timeline
	 */
	getLoadStates(): LayerLoadReport[] {
		const reports: LayerLoadReport[] = []
		for (const layer of this.layers.values()) {
			const state = layer.getLoadState()
			if (state) {
				reports.push({ id: layer.name, visible: layer.visible, ...state })
			}
		}
		for (const [id, provider] of this.externalLoadStateProviders) {
			const report = provider()
			if (report) {
				reports.push({ id, ...report })
			}
		}
		return reports
	}

	/**
	 * Wrap the Arrow downloader so the layer's load state follows the
	 * sync: "downloading" (with the in-flight shard sizes from the
	 * manifest) while any shard is on the wire, "ingesting" otherwise.
	 * The layer drives the downloads from applyCheckpoint/applyDelta, so
	 * this is the one place the manager sees them start and finish
	 */
	private trackedDownloader(layer: DatasetLayer<DatasetFeature>, manifest: DatasetManifest): ArrowDownloader {
		const sizes = new Map<string, number>()
		const remember = (shard: { url?: string; path: string; sizeBytes: number } | undefined) => {
			if (shard) sizes.set(shard.url ?? `${Constants.baseStorageUrl}/${shard.path}`, shard.sizeBytes)
		}
		manifest.checkpoint?.shards?.forEach(remember)
		manifest.deltas?.forEach(delta => {
			remember(delta.upserts)
			remember(delta.deletes)
		})

		const inFlight = new Set<string>()
		const publish = () => {
			if (inFlight.size === 0) {
				layer.setLoadPhase("sync", "ingesting")
				return
			}
			let bytes = 0
			for (const url of inFlight) bytes += sizes.get(url) ?? 0
			layer.setLoadPhase("sync", "downloading", { detail: bytes > 0 ? formatBytes(bytes) : undefined })
		}

		return async (url: string) => {
			inFlight.add(url)
			publish()
			try {
				return await this.downloadArrowRows(url)
			} finally {
				inFlight.delete(url)
				publish()
			}
		}
	}

	/**
	 * Timeline lanes for all currently visible time-based layers, keyed by
	 * layer name: overall availability plus frame times and loaded spans.
	 * Consumed by the timeline scrubber UI.
	 */
	getVisibleTimeRanges(): TimeLane[] {
		const lanes: TimeLane[] = []
		for (const layer of this.layers.values()) {
			if (!layer.visible) continue
			const range = layer.getAvailableTimeRange()
			if (range && range.endMs > range.startMs) {
				const detail = layer.getTimeLaneDetail()
				lanes.push({
					id: layer.name,
					...range,
					frameTimesMs: detail?.frameTimesMs ?? [],
					loadedRanges: detail?.loadedRanges ?? [],
					displayedFrameMs: detail?.displayedFrameMs,
				})
			}
		}
		for (const [id, provider] of this.externalTimeRangeProviders) {
			const range = provider()
			if (range && range.endMs > range.startMs) {
				lanes.push({
					id,
					startMs: range.startMs,
					endMs: range.endMs,
					frameTimesMs: range.frameTimesMs ?? [],
					loadedRanges: range.loadedRanges ?? [],
					displayedFrameMs: range.displayedFrameMs,
				})
			}
		}
		return lanes
	}

	/**
	 * Frame start times of the primary visible frame-based layer, for the
	 * timeline's prev/next frame stepping. Precedence favors the finest
	 * cadence the user is likely watching (radar before clouds before
	 * 3-hourly pressure). Empty when no frame-based layer is visible.
	 */
	getPrimaryFrameTimes(): number[] {
		const lanes = this.getVisibleTimeRanges()
		for (const id of cFrameStepPrecedence) {
			const lane = lanes.find(l => l.id === id)
			if (lane && lane.frameTimesMs.length > 1) {
				return this.withExtensionFrames(lane, lanes)
			}
		}
		const any = lanes.find(l => l.frameTimesMs.length > 1)
		return any ? this.withExtensionFrames(any, lanes) : []
	}

	// The lane's frames followed by its extension lane's frames past the
	// last one (cFrameLaneExtensions), so stepping runs from the observed
	// radar straight into the forecast
	private withExtensionFrames(lane: TimeLane, lanes: TimeLane[]): number[] {
		const extensionId = cFrameLaneExtensions[lane.id]
		const extension = extensionId ? lanes.find(l => l.id === extensionId) : undefined
		if (!extension || extension.frameTimesMs.length === 0) {
			return lane.frameTimesMs
		}
		const last = lane.frameTimesMs[lane.frameTimesMs.length - 1]
		return lane.frameTimesMs.concat(extension.frameTimesMs.filter(ms => ms > last))
	}

	/**
	 * Unregister a dataset layer
	 */
	unregisterLayer(name: string): void {
		const layer = this.layers.get(name)
		if (layer) {
			this.stopLayerPolling(name)
			this.container.removeNode(layer)
			layer.dispose()
			this.layers.delete(name)
			console.log(`[DatasetLayerManager] Unregistered layer: ${name}`)
		}
	}

	// ==================== Dataset Sync ====================

	/**
	 * Run the initial sync and start polling, self-healing on transient
	 * failures (network blips, cold database) instead of leaving the session
	 * permanently idle until the user reloads the page.
	 *
	 * syncAllLayers is idempotent (create-if-not-exists tables, cached fetches,
	 * version-guarded checkpoints), so retrying the whole operation is safe.
	 */
	async syncAndStartPolling(): Promise<void> {
		const baseDelayMs = 2_000
		const maxDelayMs = 60_000
		let attempt = 0

		while (true) {
			try {
				await this.syncAllLayers()
				this.startPolling()
				return
			} catch (err) {
				attempt += 1
				const delay = Math.min(maxDelayMs, baseDelayMs * 2 ** (attempt - 1))
				console.error(
					`[DatasetLayerManager] Initial sync failed (attempt ${attempt}), retrying in ${Math.round(delay / 1000)}s:`,
					err
				)
				await this.sleep(delay)
			}
		}
	}

	/**
	 * Initial sync of all registered layers that need it.
	 * Creates tables, fetches catalog, and applies checkpoints.
	 *
	 * For optimal UX:
	 * 1. First, refresh all layers to show cached data immediately
	 * 2. Then, sync each layer independently and refresh when new data arrives
	 */
	async syncAllLayers(): Promise<void> {
		await this.createTables()

		// Get layers that need sync
		const layersToSync = Array.from(this.layers.values())
			.filter(layer => layer.getSyncConfig()?.needsSync)

		// Phase 1: Immediately refresh all layers to show cached data
		// This gives users instant feedback while fresh data is fetched
		const initialRefreshJobs = layersToSync.map(layer => async () => {
			try {
				await this.refreshLayer(layer.name)
			} catch {
				// Silently ignore - layer may have no cached data yet
			}
		})
		await this.runWithConcurrency(initialRefreshJobs, 5, job => job())

		// Phase 2: Fetch catalog and sync each layer independently
		// Each layer refreshes immediately after its data is updated
		const catalogUrl = `${this.baseUrl}/catalog.json`
		const catalog = await this.retryAsync(
			() => this.fetchJsonCached<Catalog>(catalogUrl),
			"catalog fetch"
		)
		const byId = new Map(catalog.datasets.map(d => [d.datasetId, d]))

		// Cache the catalog for use by polling (avoids re-fetching on every poll)
		this.catalogCache = byId

		// Sync each layer (including sub-datasets) and refresh when complete.
		// Visible layers go first so what the user has enabled paints within
		// seconds of startup; hidden layers follow, and bulk reference data
		// (deferInitialSync) last so its multi-megabyte checkpoints and long
		// ingest never sit ahead of a visible layer's small sync in the
		// worker pool or on the connection
		const failedLayers: string[] = []
		const syncJobs = this.initialSyncOrder(layersToSync).map(layer => async () => {
			try {
				await this.syncLayerWithSubDatasets(layer, byId)
			} catch (err) {
				console.error(`[DatasetLayerManager] Failed syncing ${layer.name}:`, err)
				layer.setLoadPhase("sync", "error", { detail: "Retrying" })
				failedLayers.push(layer.name)
			}
		})

		await this.runWithConcurrency(syncJobs, 3, job => job())

		// Retry layers whose initial sync failed. Polling layers self-heal on
		// their next poll, but non-polling layers (pollIntervalMs: 0, e.g.
		// volcanoes/satellites/cities) would otherwise stay missing for the
		// entire session after a single transient failure on a fresh load.
		if (failedLayers.length > 0) {
			void this.retryFailedLayers(failedLayers)
		}
	}

	/**
	 * Order layers for the initial sync: visible first, then hidden, then
	 * layers flagged deferInitialSync. Registration order is preserved
	 * within each group.
	 */
	private initialSyncOrder(layers: DatasetLayer<DatasetFeature>[]): DatasetLayer<DatasetFeature>[] {
		const rank = (layer: DatasetLayer<DatasetFeature>): number => {
			if (layer.getSyncConfig()?.deferInitialSync) return 2
			return layer.visible ? 0 : 1
		}
		return layers
			.map((layer, index) => ({ layer, index, rank: rank(layer) }))
			.sort((a, b) => a.rank - b.rank || a.index - b.index)
			.map(entry => entry.layer)
	}

	/**
	 * Retry layers that failed their initial sync, with exponential backoff.
	 *
	 * Runs in the background (not awaited) so it never blocks startPolling. Uses
	 * the cached catalog via syncLayerByName, so it is idempotent and version-
	 * guarded: already-synced layers no-op, and a layer drops out of the retry
	 * set as soon as it succeeds.
	 */
	private async retryFailedLayers(layerNames: string[]): Promise<void> {
		const maxAttempts = 6
		const baseDelayMs = 5_000
		const maxDelayMs = 60_000

		let pending = [...layerNames]
		for (let attempt = 1; attempt <= maxAttempts && pending.length > 0; attempt++) {
			const delay = Math.min(maxDelayMs, baseDelayMs * 2 ** (attempt - 1))
			await this.sleep(delay)

			const stillFailing: string[] = []
			for (const name of pending) {
				try {
					await this.syncLayerByName(name)
				} catch (err) {
					console.error(
						`[DatasetLayerManager] Retry sync failed for ${name} (attempt ${attempt}/${maxAttempts}):`,
						err
					)
					this.layers.get(name)?.setLoadPhase("sync", "error", { detail: "Retrying" })
					stillFailing.push(name)
				}
			}
			pending = stillFailing
		}

		if (pending.length > 0) {
			console.error(
				`[DatasetLayerManager] Gave up syncing after ${maxAttempts} retries: ${pending.join(", ")}`
			)
			for (const name of pending) {
				this.layers.get(name)?.setLoadPhase("sync", "error", { detail: "Failed" })
			}
		}
	}

	/**
	 * Sync a layer and all its sub-datasets, then refresh scene objects
	 * This ensures sub-dataset data is available before creating scene objects
	 */
	private async syncLayerWithSubDatasets(
		layer: DatasetLayer<DatasetFeature>,
		catalog: Map<string, Catalog["datasets"][number]>
	): Promise<void> {
		const config = layer.getSyncConfig()!
		let anyChanges = false

		// A failed attempt may have left the layer in "error"; a fresh pass
		// starts clean and only re-enters a phase when work actually happens
		layer.clearLoadPhase("sync")
		try {
			// Sync primary dataset
			const entry = catalog.get(config.datasetId)
			if (entry) {
				const changed = await this.syncLayerIfNeeded(layer, entry)
				anyChanges = anyChanges || changed
			}

			// Sync sub-datasets sequentially to ensure they're all ready before refresh
			const subDatasetIds = layer.getSubDatasetIds?.() ?? []
			for (const subId of subDatasetIds) {
				if (subId === config.datasetId) continue
				const subEntry = catalog.get(subId)
				if (subEntry) {
					const changed = await this.syncSubDatasetIfNeeded(layer, subId, subEntry)
					anyChanges = anyChanges || changed
				}
			}

			// If any data changed, notify layer and refresh scene objects
			if (anyChanges) {
				layer.setLoadPhase("sync", "ingesting")
				await layer.onSyncComplete()
				await this.refreshLayer(layer.name)
			}
		} finally {
			// The chunked scene build, if any, carries on under its own source
			layer.clearLoadPhase("sync")
		}
	}

	/**
	 * Sync a single layer by name (for polling)
	 * Also syncs sub-datasets if the layer has them
	 * Uses cached catalog from initial sync - no need to re-fetch catalog.json
	 */
	async syncLayerByName(name: string): Promise<void> {
		const layer = this.layers.get(name)
		if (!layer) return

		const config = layer.getSyncConfig()
		if (!config?.needsSync) return

		// Use cached catalog (populated during syncAllLayers)
		if (!this.catalogCache) {
			// This can happen if syncAllLayers failed or hasn't completed yet
			// Don't log every poll attempt - just skip silently
			return
		}

		let anyChanges = false

		layer.clearLoadPhase("sync")
		try {
			// Sync primary dataset
			const entry = this.catalogCache.get(config.datasetId)
			if (entry) {
				const changed = await this.syncLayerIfNeeded(layer, entry)
				anyChanges = anyChanges || changed
			}

			// Sync sub-datasets
			const subDatasetIds = layer.getSubDatasetIds?.() ?? []
			for (const subId of subDatasetIds) {
				if (subId === config.datasetId) continue // Skip primary
				const subEntry = this.catalogCache.get(subId)
				if (subEntry) {
					try {
						const changed = await this.syncSubDatasetIfNeeded(layer, subId, subEntry)
						anyChanges = anyChanges || changed
					} catch (err) {
						console.error(`[DatasetLayerManager] Failed syncing sub-dataset ${subId}:`, err)
					}
				}
			}

			// Notify layer and refresh scene objects only if something changed
			if (anyChanges) {
				layer.setLoadPhase("sync", "ingesting")
				await layer.onSyncComplete()
				await this.refreshLayer(layer.name)
			}
		} finally {
			layer.clearLoadPhase("sync")
		}
	}

	/**
	 * Sync layer if version changed, returns true if data was updated
	 *
	 * Strategy:
	 * 1. If checkpoint version changed, apply checkpoint (unless deltaOnlyIngest) or re-fetch the same
	 *    checkpoint URL when latestVersion moved (in-place CDN overwrite, checkpoint-only layers).
	 * 2. After that, apply delta when supportsDelta and manifest provides a matching delta.
	 * 3. appliedVersion tracks latestVersion except mid-delta sync (checkpoint applied, delta pending).
	 */
	private async syncLayerIfNeeded(
		layer: DatasetLayer<DatasetFeature>,
		entry: Catalog["datasets"][number]
	): Promise<boolean> {
		const config = layer.getSyncConfig()!

		const manifestUrl = entry.manifestUrl ?? `${Constants.baseStorageUrl}/${entry.manifestPath}`
		const manifest = await this.retryAsync(
			() => this.fetchJsonCached<DatasetManifest>(manifestUrl),
			`manifest fetch ${config.datasetId}`
		)

		const deltaOnly = config.deltaOnlyIngest === true && config.supportsDelta === true
		if (!manifest.checkpoint?.version) {
			throw new Error(`Manifest for ${config.datasetId} has no checkpoint version`)
		}
		if (!deltaOnly && !manifest.checkpoint?.shards?.length) {
			throw new Error(`Manifest for ${config.datasetId} has no checkpoint shards`)
		}
		if (deltaOnly && manifest.checkpoint.version === manifest.latestVersion) {
			throw new Error(
				`Manifest for ${config.datasetId} is delta-only but latestVersion equals checkpoint.version`
			)
		}

		let appliedVersion = await this.getAppliedVersion(config.datasetId)
		// Publication stamp is always latestVersion. checkpoint.version may stay fixed for in-place CDN
		// overwrites (aurora, clouds, city_weather); clients must not key off checkpoint.version alone.
		const targetVersion = manifest.latestVersion
		if (appliedVersion === targetVersion) {
			// Self-heal: a previous sync may have recorded the version even though no
			// rows landed (e.g. an empty shard). Unmark it so this sync re-downloads.
			if (await this.isDatasetEmpty(layer, config.datasetId, manifest, deltaOnly)) {
				console.warn(
					`[DatasetLayerManager] ${config.datasetId} marked applied (v${appliedVersion}) but has no rows, clearing sync state to force re-download`
				)
				await this.clearSyncState(config.datasetId)
				appliedVersion = null
			} else {
				return false // No changes needed
			}
		}

		// Check if we need to apply checkpoint (new checkpoint id, or delta-only logical marker)
		const appliedCheckpoint = await this.getAppliedCheckpointVersion(config.datasetId)
		const needsCheckpoint = !appliedCheckpoint || appliedCheckpoint !== manifest.checkpoint.version
		// Checkpoint-only datasets can overwrite the same URL while bumping latestVersion (aurora, clouds, …)
		const needsCheckpointFileRefresh =
			config.supportsDelta === false && !needsCheckpoint && appliedVersion !== manifest.latestVersion

		let didApplyCheckpoint = false
		if (needsCheckpoint || needsCheckpointFileRefresh) {
			if (deltaOnly) {
				console.log(
					`[DatasetLayerManager] ${layer.name}: delta-only ingest, logical checkpoint v${manifest.checkpoint.version}`
				)
				await this.setAppliedCheckpointVersion(config.datasetId, manifest.checkpoint.version)
			} else {
				if (needsCheckpointFileRefresh) {
					console.log(
						`[DatasetLayerManager] ${layer.name}: re-fetching checkpoint (latest ${manifest.latestVersion})`
					)
				} else {
					console.log(`[DatasetLayerManager] ${layer.name}: applying checkpoint v${manifest.checkpoint.version}`)
				}
				layer.setLoadPhase("sync", "ingesting")
				await layer.applyCheckpoint(manifest, this.trackedDownloader(layer, manifest))
				// Don't mark an empty result as applied - throwing here leaves the
				// version unset so retries (and the next page load) re-download.
				if (await this.isDatasetEmpty(layer, config.datasetId, manifest, deltaOnly)) {
					throw new Error(
						`Checkpoint v${manifest.checkpoint.version} for ${config.datasetId} produced no rows, not marking as applied`
					)
				}
				if (needsCheckpoint) {
					await this.setAppliedCheckpointVersion(config.datasetId, manifest.checkpoint.version)
				}
				didApplyCheckpoint = true
			}
		}

		// After checkpoint is applied (or was already applied), check if we need a delta to reach latestVersion
		let didApplyDelta = false
		if (manifest.checkpoint.version !== manifest.latestVersion && config.supportsDelta && manifest.deltas?.length) {
			const delta = manifest.deltas.find(
				d => d.fromVersion === manifest.checkpoint.version && d.toVersion === manifest.latestVersion
			)

			if (delta) {
				console.log(`[DatasetLayerManager] ${layer.name}: applying delta v${delta.fromVersion} -> v${delta.toVersion}`)
				layer.setLoadPhase("sync", "ingesting")
				await layer.applyDelta(delta, this.trackedDownloader(layer, manifest))
				if (await this.isDatasetEmpty(layer, config.datasetId, manifest, deltaOnly)) {
					throw new Error(
						`Delta v${delta.toVersion} for ${config.datasetId} produced no rows, not marking as applied`
					)
				}
				didApplyDelta = true
			} else {
				// No delta available - data will be at checkpoint version only
				console.log(`[DatasetLayerManager] ${layer.name}: no delta available, data at checkpoint v${manifest.checkpoint.version}`)
			}
		}

		// Delta-capable: if we only applied checkpoint, stay at checkpoint.version until delta runs.
		// Checkpoint-only: always record latestVersion (content revs without changing checkpoint.version).
		const newAppliedVersion =
			config.supportsDelta && !didApplyDelta ? manifest.checkpoint.version : manifest.latestVersion
		await this.setAppliedVersion(config.datasetId, newAppliedVersion)

		return didApplyCheckpoint || didApplyDelta
	}

	/**
	 * Sync sub-dataset if version changed, returns true if data was updated
	 */
	private async syncSubDatasetIfNeeded(
		layer: DatasetLayer<DatasetFeature>,
		datasetId: string,
		entry: Catalog["datasets"][number]
	): Promise<boolean> {
		const manifestUrl = entry.manifestUrl ?? `${Constants.baseStorageUrl}/${entry.manifestPath}`
		const manifest = await this.retryAsync(
			() => this.fetchJsonCached<DatasetManifest>(manifestUrl),
			`manifest fetch ${datasetId}`
		)

		if (!manifest.checkpoint?.shards?.length) {
			throw new Error(`Manifest for ${datasetId} has no checkpoint shards`)
		}

		const appliedVersion = await this.getAppliedVersion(datasetId)
		if (appliedVersion === manifest.latestVersion) {
			// Self-heal: unmark sub-datasets that were marked applied while empty
			if (!(await this.isDatasetEmpty(layer, datasetId, manifest, false))) {
				return false // No changes needed
			}
			console.warn(
				`[DatasetLayerManager] ${datasetId} marked applied (v${appliedVersion}) but has no rows, clearing sync state to force re-download`
			)
			await this.clearSyncState(datasetId)
		}

		console.log(`[DatasetLayerManager] ${layer.name}: syncing sub-dataset ${datasetId} to latest ${manifest.latestVersion}`)

		if (layer.applySubDatasetCheckpoint) {
			layer.setLoadPhase("sync", "ingesting")
			await layer.applySubDatasetCheckpoint(datasetId, manifest, this.trackedDownloader(layer, manifest))
			if (await this.isDatasetEmpty(layer, datasetId, manifest, false)) {
				throw new Error(
					`Checkpoint for sub-dataset ${datasetId} produced no rows, not marking as applied`
				)
			}
		}

		await this.setAppliedVersion(datasetId, manifest.latestVersion)
		return true // Changes applied
	}

	// ==================== Polling ====================

	/**
	 * Start polling for all layers that have a poll interval
	 */
	startPolling(): void {
		// Safety check: don't start polling if catalog wasn't loaded
		if (!this.catalogCache) {
			console.warn("[DatasetLayerManager] Cannot start polling - catalog not loaded. Try refreshing the page.")
			return
		}

		this.isPaused = document.hidden

		// Set up visibility handler
		if (this.visibilityHandler === null) {
			this.visibilityHandler = this.handleVisibilityChange.bind(this)
			document.addEventListener("visibilitychange", this.visibilityHandler)
		}

		// Start polling for each layer that needs it
		for (const layer of this.layers.values()) {
			const config = layer.getSyncConfig()
			if (config?.pollIntervalMs && config.pollIntervalMs > 0) {
				this.startLayerPolling(layer.name, config.pollIntervalMs)
			}
		}
	}

	/**
	 * Stop all polling
	 */
	stopPolling(): void {
		for (const name of this.layerTimers.keys()) {
			this.stopLayerPolling(name)
		}

		if (this.visibilityHandler) {
			document.removeEventListener("visibilitychange", this.visibilityHandler)
			this.visibilityHandler = null
		}
	}

	private startLayerPolling(name: string, pollMs: number): void {
		let timerInfo = this.layerTimers.get(name)

		if (!timerInfo) {
			timerInfo = { timer: null, pollMs, active: true, lastSyncTime: Date.now() }
			this.layerTimers.set(name, timerInfo)
		}

		timerInfo.active = true
		timerInfo.pollMs = pollMs
		timerInfo.lastSyncTime = Date.now()  // We just synced

		if (this.isPaused) return

		this.startLayerTimer(name)
	}

	private stopLayerPolling(name: string): void {
		const timerInfo = this.layerTimers.get(name)
		if (timerInfo) {
			timerInfo.active = false
			this.stopLayerTimer(name)
		}
	}

	/**
	 * Start the layer timer, accounting for elapsed time since last sync.
	 * @param initialDelayMs Optional delay for first tick. If not provided, calculates based on lastSyncTime.
	 */
	private startLayerTimer(name: string, initialDelayMs?: number): void {
		const timerInfo = this.layerTimers.get(name)
		if (timerInfo?.timer !== null) return

		const tick = async () => {
			timerInfo.lastSyncTime = Date.now()
			try {
				await this.syncLayerByName(name)
			} catch (err) {
				console.error(`[DatasetLayerManager] ${name} sync failed:`, err)
				// The next poll retries; show that rather than a silent gap
				this.layers.get(name)?.setLoadPhase("sync", "error", { detail: "Retrying" })
			}
		}

		// Calculate delay until first tick
		let firstTickDelay: number
		if (initialDelayMs !== undefined) {
			firstTickDelay = initialDelayMs
		} else {
			// Calculate remaining time based on when we last synced
			const elapsed = Date.now() - timerInfo.lastSyncTime
			const remaining = timerInfo.pollMs - elapsed
			firstTickDelay = Math.max(0, remaining)
		}

		// If we're past due, tick immediately and start regular interval
		if (firstTickDelay <= 0) {
			void tick()
			timerInfo.timer = window.setInterval(() => void tick(), timerInfo.pollMs)
		} else {
			// Use setTimeout for adjusted first tick, then switch to setInterval
			timerInfo.timer = window.setTimeout(() => {
				// Guard against race condition: if timer was stopped while setTimeout was pending,
				// don't create the setInterval (timerInfo.timer would be null after stopLayerTimer)
				if (timerInfo.timer === null) return

				void tick()
				// After first tick, switch to regular interval
				timerInfo.timer = window.setInterval(() => void tick(), timerInfo.pollMs)
			}, firstTickDelay)
		}
	}

	private stopLayerTimer(name: string): void {
		const timerInfo = this.layerTimers.get(name)
		if (timerInfo && timerInfo.timer !== null) {
			// Clear both timeout and interval (only one will be active)
			window.clearTimeout(timerInfo.timer)
			window.clearInterval(timerInfo.timer)
			timerInfo.timer = null
		}
	}

	private handleVisibilityChange(): void {
		const isHidden = document.hidden

		if (isHidden && !this.isPaused) {
			console.log("[DatasetLayerManager] Page hidden, pausing polling")
			this.isPaused = true
			this.pausedAt = Date.now()
			for (const name of this.layerTimers.keys()) {
				this.stopLayerTimer(name)
			}
		} else if (!isHidden && this.isPaused) {
			const hiddenDuration = Date.now() - this.pausedAt
			console.log(`[DatasetLayerManager] Page visible, resuming polling (hidden for ${Math.round(hiddenDuration / 1000)}s)`)
			this.isPaused = false

			for (const [name, timerInfo] of this.layerTimers.entries()) {
				if (timerInfo.active) {
					// Calculate how much time has passed since last sync
					const timeSinceSync = Date.now() - timerInfo.lastSyncTime

					if (timeSinceSync >= timerInfo.pollMs) {
						// Past due - sync immediately and restart timer at full interval
						console.log(`[DatasetLayerManager] ${name}: past due by ${Math.round((timeSinceSync - timerInfo.pollMs) / 1000)}s, syncing now`)
						timerInfo.lastSyncTime = Date.now()
						void this.syncLayerByName(name)
						this.startLayerTimer(name, timerInfo.pollMs)
					} else {
						// Not yet due - start timer with remaining time
						const remaining = timerInfo.pollMs - timeSinceSync
						console.log(`[DatasetLayerManager] ${name}: ${Math.round(remaining / 1000)}s until next sync`)
						this.startLayerTimer(name, remaining)
					}
				}
			}
		}
	}

	// ==================== HTTP Caching & Arrow Download ====================

	private proxyUrl(url: string): string {
		return datasetUrl(url)
	}

	async fetchJsonCached<T>(url: string): Promise<T> {
		const cached = await this.getHttpCache(url)
		const lastModified = cached?.last_modified ?? undefined

		const headers: Record<string, string> = {}
		if (lastModified) {
			headers["If-Modified-Since"] = lastModified
		}

		const res = await fetch(this.proxyUrl(url), { headers })
		if (res.status === 304) {
			if (!cached?.body) throw new Error(`304 for ${url} but no cached body`)
			return JSON.parse(new TextDecoder().decode(cached.body)) as T
		}

		if (!res.ok) throw new Error(`Failed to fetch ${url}: ${res.status}`)
		const text = await res.text()
		const newLastModified = res.headers.get("Last-Modified")
		await this.setHttpCache(url, newLastModified, new TextEncoder().encode(text))
		return JSON.parse(text) as T
	}

	async downloadArrowRows(url: string): Promise<ArrowRow[]> {
		return this.retryAsync(async () => {
			// cache: "no-cache" forces revalidation with the CDN. Checkpoint and
			// delta files are rewritten in place under stable versioned URLs
			// (aurora/clouds/buoys/satellites checkpoints, earthquakes/mrms/imerg
			// deltas), and were historically served with immutable cache headers,
			// so the default browser cache could pin a stale copy for up to 24h
			// while the manifest kept advancing. Arrow downloads only happen when
			// a version changes, so skipping the browser cache costs nothing.
			const res = await fetch(this.proxyUrl(url), { cache: "no-cache" })
			if (!res.ok) throw new Error(`Failed to fetch ${url}: ${res.status}`)
			const bytes = new Uint8Array(await res.arrayBuffer())
			const table = tableFromIPC(bytes)
			return table.toArray() as ArrowRow[]
		}, `download ${url}`)
	}

	private async getHttpCache(url: string): Promise<HttpCacheRow | null> {
		const rows = await Database.db.exec(
			"select url, last_modified, body from http_resource_cache where url = ?",
			[url],
			"low"
		) as HttpCacheRow[]

		return rows[0] ?? null
	}

	private async setHttpCache(url: string, lastModified: string | null, body: Uint8Array): Promise<void> {
		await Database.db.exec(
			`insert into http_resource_cache (url, last_modified, body)
				values (?, ?, ?)
				on conflict(url) do update set
					last_modified = excluded.last_modified,
					body = excluded.body`,
			[url, lastModified, body],
			"low"
		)
	}

	/**
	 * Create the metadata and per-layer tables (create-if-not-exists,
	 * idempotent). Callers starting from a fresh database (the embed's
	 * in-memory SQLite) run this before making layers visible, because
	 * several layers query their table the moment they become visible;
	 * syncAllLayers re-runs it harmlessly.
	 */
	async createTables(): Promise<void> {
		const db = Database.getDb()
		if (!db) {
			throw new Error("[DatasetLayerManager] Database not ready, cannot create tables")
		}

		await this.ensureMetadataTables()

		for (const layer of this.layers.values()) {
			await layer.createTable(db)
		}
	}

	// ==================== Version Tracking ====================

	private async ensureMetadataTables(): Promise<void> {
		await Database.db.execMany([
			{
				sql: `create table if not exists dataset_sync_state (
					dataset_id text primary key,
					version text not null,
					updated_at text not null
				)`
			},
			{
				sql: `create table if not exists dataset_checkpoint_state (
					dataset_id text primary key,
					checkpoint_version text not null,
					updated_at text not null
				)`
			},
			{
				sql: `create table if not exists http_resource_cache (
					url text primary key,
					last_modified text,
					body blob
				)`
			}
		])
	}

	async getAppliedVersion(datasetId: string): Promise<string | null> {
		const rows = await Database.db.exec(
			"select version from dataset_sync_state where dataset_id = ?",
			[datasetId],
			"critical"
		) as Array<{ version: string }>

		return rows[0]?.version ?? null
	}

	async setAppliedVersion(datasetId: string, version: string): Promise<void> {
		await Database.db.exec(
			`insert into dataset_sync_state (dataset_id, version, updated_at)
				values (?, ?, ?)
				on conflict(dataset_id) do update set
					version = excluded.version,
					updated_at = excluded.updated_at`,
			[datasetId, version, new Date().toISOString()],
			"critical"
		)
	}

	async getAppliedCheckpointVersion(datasetId: string): Promise<string | null> {
		const rows = await Database.db.exec(
			"select checkpoint_version from dataset_checkpoint_state where dataset_id = ?",
			[datasetId],
			"low"
		) as Array<{ checkpoint_version: string }>

		return rows[0]?.checkpoint_version ?? null
	}

	/**
	 * True when the manifest says the dataset should contain rows but the
	 * layer's stored table is empty. Layers opt in by overriding
	 * getStoredRowCount; layers that return null are never considered empty.
	 */
	private async isDatasetEmpty(
		layer: DatasetLayer<DatasetFeature>,
		datasetId: string,
		manifest: DatasetManifest,
		deltaOnly: boolean
	): Promise<boolean> {
		const expected = this.expectedRowCount(manifest, deltaOnly)
		if (expected <= 0) return false

		const stored = await layer.getStoredRowCount(datasetId)
		return stored === 0
	}

	/**
	 * Expected row count for a dataset at manifest.latestVersion.
	 * Delta-only layers never download checkpoint shards, so the delta that
	 * produces latestVersion is the source of truth for those.
	 */
	private expectedRowCount(manifest: DatasetManifest, deltaOnly: boolean): number {
		if (deltaOnly) {
			const delta = manifest.deltas?.find(d => d.toVersion === manifest.latestVersion)
			return delta?.upserts.rowCount ?? 0
		}
		return (manifest.checkpoint?.shards ?? []).reduce((sum, shard) => sum + (shard.rowCount ?? 0), 0)
	}

	/**
	 * Remove version tracking for a dataset so the next sync re-downloads it.
	 */
	private async clearSyncState(datasetId: string): Promise<void> {
		await Database.db.exec(
			"delete from dataset_sync_state where dataset_id = ?",
			[datasetId],
			"critical"
		)
		await Database.db.exec(
			"delete from dataset_checkpoint_state where dataset_id = ?",
			[datasetId],
			"low"
		)
	}

	async setAppliedCheckpointVersion(datasetId: string, checkpointVersion: string): Promise<void> {
		await Database.db.exec(
			`insert into dataset_checkpoint_state (dataset_id, checkpoint_version, updated_at)
				values (?, ?, ?)
				on conflict(dataset_id) do update set
					checkpoint_version = excluded.checkpoint_version,
					updated_at = excluded.updated_at`,
			[datasetId, checkpointVersion, new Date().toISOString()],
			"low"
		)
	}

	// ==================== Utility ====================

	private sleep(ms: number): Promise<void> {
		return new Promise(resolve => setTimeout(resolve, ms))
	}

	/**
	 * Retry an async operation a few times with exponential backoff.
	 * Used for network operations (catalog/manifest/shard downloads) so a single
	 * transient failure on a fresh signup doesn't drop the dataset for the session.
	 */
	private async retryAsync<T>(
		fn: () => Promise<T>,
		label: string,
		attempts = 3,
		baseDelayMs = 1_000
	): Promise<T> {
		let lastErr: unknown
		for (let attempt = 1; attempt <= attempts; attempt++) {
			try {
				return await fn()
			} catch (err) {
				lastErr = err
				if (attempt < attempts) {
					const delay = baseDelayMs * 2 ** (attempt - 1)
					console.warn(`[DatasetLayerManager] ${label} failed (attempt ${attempt}/${attempts}), retrying in ${delay}ms:`, err)
					await this.sleep(delay)
				}
			}
		}
		throw lastErr
	}

	private async runWithConcurrency<T>(
		items: T[],
		concurrency: number,
		fn: (item: T) => Promise<void>
	): Promise<void> {
		const limit = Math.max(1, Math.floor(concurrency))
		let idx = 0

		const workers = new Array(limit).fill(null).map(async () => {
			while (true) {
				const currentIdx = idx
				if (currentIdx >= items.length) return
				idx += 1
				await fn(items[currentIdx])
			}
		})

		await Promise.all(workers)
	}

	// ==================== Scene Object Management ====================

	/**
	 * Subscribe to refreshes of a layer. Fired after the layer's scene objects
	 * have been rebuilt from the database (initial cached-data load, post-sync,
	 * or manual refresh), i.e. whenever table reads may return new data.
	 * Returns an unsubscribe function.
	 */
	addRefreshListener(name: string, listener: () => void): () => void {
		let listeners = this.refreshListeners.get(name)
		if (!listeners) {
			listeners = new Set()
			this.refreshListeners.set(name, listeners)
		}
		listeners.add(listener)
		return () => {
			this.refreshListeners.get(name)?.delete(listener)
		}
	}

	/**
	 * Refresh a specific layer's scene objects from database
	 */
	async refreshLayer(name: string): Promise<void> {
		const layer = this.layers.get(name)
		if (!layer) {
			console.warn(`[DatasetLayerManager] Layer not registered: ${name}`)
			return
		}

		try {
			// console.log(`[DatasetLayerManager] Refreshing layer: ${name}`)
			await layer.refresh()
		} catch (error) {
			console.error(`[DatasetLayerManager] Error refreshing ${name}:`, error)
			return
		}

		const listeners = this.refreshListeners.get(name)
		if (listeners) {
			for (const listener of listeners) {
				try {
					listener()
				} catch (error) {
					console.error(`[DatasetLayerManager] Refresh listener error for ${name}:`, error)
				}
			}
		}
	}

	/**
	 * Refresh all registered layers
	 */
	async refreshAll(): Promise<void> {
		const promises = Array.from(this.layers.keys()).map(name =>
			this.refreshLayer(name)
		)
		await Promise.all(promises)
	}

	/**
	 * Get a layer by name
	 */
	getLayer(name: string): DatasetLayer<DatasetFeature> | undefined {
		return this.layers.get(name)
	}

	/**
	 * Get all layer names
	 */
	getLayerNames(): string[] {
		return Array.from(this.layers.keys())
	}

	/**
	 * Get all layers
	 */
	getLayers(): DatasetLayer<DatasetFeature>[] {
		return Array.from(this.layers.values())
	}

	/**
	 * Set layer visibility
	 */
	setLayerVisible(name: string, visible: boolean): void {
		const layer = this.layers.get(name)
		if (layer) {
			layer.visible = visible
		}
	}

	/**
	 * Get a scene object by ID from a specific layer
	 */
	getSceneObject(name: string, featureId: string): DatasetSceneObject<DatasetFeature> | undefined {
		const layer = this.layers.get(name)
		return layer ? layer.getSceneObject(featureId) : undefined
	}

	/** Monotonic token so a newer focus request cancels older pending retries */
	private focusRequestToken = 0

	/**
	 * Focus on a scene object by layer and feature ID.
	 * If the scene object isn't currently loaded, attempts on-demand loading from the database.
	 *
	 * options.instant places the camera at the node's focus destination
	 * immediately instead of flying there - used by embeds, where a launch
	 * animation on every mount is noise (the camera sits at a fallback pose
	 * until the entity's data syncs, then cuts to the framed view).
	 */
	focusSceneObject(name: string, featureId: string, options?: FocusSceneObjectOptions): boolean {
		// Any new focus request supersedes pending retries from a previous one
		const token = ++this.focusRequestToken

		// Try to get existing scene object
		const sceneObject = this.getSceneObject(name, featureId)
		if (sceneObject) {
			return this.doFocus(sceneObject, name, featureId, options)
		}

		// Scene object not found - try on-demand loading
		const layer = this.layers.get(name)
		if (!layer) {
			console.warn(`[DatasetLayerManager] Layer not found: ${name}`)
			return false
		}

		// Attempt async loading and focus
		void this.loadAndFocus(layer, name, featureId, token, options)
		return true // Optimistic return - loading in progress
	}

	/**
	 * Load a scene object on-demand and focus on it.
	 *
	 * Retries with backoff: a launch deep-link can request a feature before the
	 * layer's initial background sync has populated the database (especially on
	 * a first visit), so a single failed lookup shouldn't be fatal. Retrying is
	 * abandoned if a newer focus request supersedes this one, or if the user
	 * starts interacting with the scene - a deep-link focus that lands many
	 * seconds after launch would yank the camera away from wherever they've
	 * navigated to in the meantime.
	 */
	private async loadAndFocus(
		layer: DatasetLayer<DatasetFeature>,
		name: string,
		featureId: string,
		token: number,
		options?: FocusSceneObjectOptions
	): Promise<boolean> {
		const retryDelaysMs = [0, 1_000, 2_000, 4_000, 8_000, 16_000, 30_000]

		// Any pointer/wheel interaction with the scene cancels pending retries
		let userInteracted = false
		const onInteraction = () => { userInteracted = true }
		this.context.canvas.addEventListener('pointerdown', onInteraction)
		this.context.canvas.addEventListener('wheel', onInteraction)

		try {
			for (const delayMs of retryDelaysMs) {
				if (delayMs > 0) {
					await new Promise(resolve => setTimeout(resolve, delayMs))
				}
				if (token !== this.focusRequestToken) {
					return false // Superseded by a newer focus request
				}
				if (userInteracted) {
					console.log(`[DatasetLayerManager] Focus retries for ${name}/${featureId} cancelled by user interaction`)
					return false
				}

				try {
					// A background sync may have built the scene object since the
					// last attempt; layers without on-demand loading (base
					// loadSceneObjectById returns undefined - earthquakes, buoys)
					// are only reachable this way
					const sceneObject = layer.getSceneObject(featureId)
						?? await layer.loadSceneObjectById(featureId)
					if (sceneObject) {
						return this.doFocus(sceneObject, name, featureId, options)
					}
				} catch (error) {
					console.error(`[DatasetLayerManager] Error loading scene object ${name}/${featureId}:`, error)
				}
			}
		} finally {
			this.context.canvas.removeEventListener('pointerdown', onInteraction)
			this.context.canvas.removeEventListener('wheel', onInteraction)
		}

		// Exhausted retries: the entity likely expired or was removed from the
		// dataset (old shared link, restored session). Tell the user instead of
		// failing silently on the default view.
		console.warn(`[DatasetLayerManager] Could not load scene object: ${name}/${featureId}`)
		engineNotifier.toast({
			title: "Target not found",
			description: "The linked item is no longer available. It may have expired or been removed from the dataset.",
			duration: 6000
		})
		clearTargetFromHistory()
		return false
	}

	/**
	 * Actually perform the focus on a scene object
	 */
	private doFocus(
		sceneObject: DatasetSceneObject<DatasetFeature>,
		name: string,
		featureId: string,
		options?: FocusSceneObjectOptions
	): boolean {
		if (options?.instant && this.doInstantFocus(sceneObject)) {
			return true
		}

		const focusable = sceneObject as { requestFocus?: () => void }
		if (typeof focusable.requestFocus === 'function') {
			focusable.requestFocus()
			return true
		}

		console.warn(`[DatasetLayerManager] Scene object does not support focus: ${name}/${featureId}`)
		return false
	}

	/**
	 * Place the camera at the node's focus destination immediately and grant
	 * focus without a transition.
	 *
	 * Mechanics: a zero-duration FocusTransition deliberately does NOT move
	 * the camera, and the entity camera state's activate() adopts whatever
	 * pose the camera is at (syncFromCamera). So instant focus writes the
	 * node's own transition target - the exact state the flight would have
	 * ended at - straight into the camera, then requests focus with
	 * preserveCurrentState so the handoff adopts that pose with no animation.
	 *
	 * Returns false when the node has no focusTransition (or no camera is
	 * set yet); the caller falls back to the normal animated path.
	 */
	private doInstantFocus(sceneObject: DatasetSceneObject<DatasetFeature>): boolean {
		const focusManager = this.context.focusManager
		const camera = focusManager.getViewportHandler()?.camera
		const node = sceneObject as Partial<FocusTarget>
		if (!camera || typeof node.focusTransition !== 'function' || typeof node.requestFocus !== 'function') {
			return false
		}

		const config = node.focusTransition(camera)
		if (!config) return false

		const target = typeof config.target === 'function' ? config.target() : config.target
		camera.state.position = target.position
		camera.state.forward = target.forward
		camera.state.up = target.up

		return focusManager.requestFocus(node as FocusTarget, { preserveCurrentState: true })
	}

	/**
	 * Clear all scene objects in all layers
	 */
	clear(): void {
		for (const layer of this.layers.values()) {
			layer.clear()
		}
	}

	/**
	 * Dispose the manager and all layers
	 */
	dispose(): void {
		this.stopPolling()

		for (const [name, layer] of this.layers.entries()) {
			layer.dispose()
			console.log(`[DatasetLayerManager] Disposed layer: ${name}`)
		}
		this.layers.clear()
	}
}
